import React from "react";
import { Link } from "react-router-dom";
import { Motif } from "@/lib/motifs";
import { imgUrl } from "@/lib/api";

/**
 * MotifCircle — a hover-triggered mini burst of realistic creatures escapes
 * from the ring on hover. Uses the same watercolor PNGs as the /motif page.
 */

// Motifs that have realistic-creature PNGs to burst.
const HAS_BURST = new Set([
  "butterfly", "dragonfly", "bee", "owl", "lotus", "hearts", "car", "leaf", "bud",
]);

const VARIANTS = {
  butterfly: ["butterfly", "butterfly-2", "butterfly-3"],
  dragonfly: ["dragonfly", "dragonfly-2", "dragonfly-3"],
  bee:       ["bee", "bee-2", "bee-3"],
};

// 5 offset "escape" vectors from centre (percent-based, applied via inline vars)
const BURST = [
  { dx: -60, dy: -80, r: -18, delay: 0,   size: 44 },
  { dx:  70, dy: -70, r:  22, delay: 60,  size: 52 },
  { dx: -80, dy:  10, r: -30, delay: 120, size: 38 },
  { dx:  90, dy:  30, r:  28, delay: 180, size: 46 },
  { dx:   0, dy: -110, r:  0, delay: 240, size: 40 },
];

const MotifCircle = ({ motifKey, label, size = "md" }) => {
  const sizes = { sm: "w-14 h-14", md: "w-24 h-24 md:w-28 md:h-28", lg: "w-32 h-32 md:w-36 md:h-36" };
  const variants = VARIANTS[motifKey] || [motifKey];
  const canBurst = HAS_BURST.has(motifKey);

  return (
    <Link
      to={`/motif/${motifKey}`}
      className="group flex flex-col items-center gap-4 relative"
      data-testid={`motif-circle-${motifKey}`}
    >
      <div className={`motif-ring ${sizes[size]} rounded-full flex items-center justify-center relative`}>
        <div className="text-ink group-hover:text-accent transition-colors duration-500">
          <Motif name={motifKey} size={54} />
        </div>

        {/* Hover burst — 5 creatures escape from the ring */}
        {canBurst && (
          <div
            className="absolute inset-0 pointer-events-none"
            data-testid={`motif-burst-${motifKey}`}
          >
            {BURST.map((b, i) => {
              const src = imgUrl(`/api/uploads/motif-creatures/${variants[i % variants.length]}.png`);
              return (
                <img
                  key={i}
                  src={src}
                  alt=""
                  aria-hidden="true"
                  className="absolute top-1/2 left-1/2 opacity-0 group-hover:opacity-100 group-hover:animate-motif-burst"
                  style={{
                    width: b.size,
                    height: "auto",
                    marginLeft: -b.size / 2,
                    marginTop: -b.size / 2,
                    ["--bdx"]: `${b.dx}px`,
                    ["--bdy"]: `${b.dy}px`,
                    ["--brot"]: `${b.r}deg`,
                    animationDelay: `${b.delay}ms`,
                    animationFillMode: "forwards",
                    filter: "drop-shadow(0 3px 6px rgba(28,26,25,0.15))",
                    zIndex: 10,
                  }}
                  draggable="false"
                />
              );
            })}
          </div>
        )}
      </div>
      <span className="nav-item">{label}</span>
    </Link>
  );
};

export default MotifCircle;
